/**
 * @file     mlsNotices.ts
 * @purpose  The board and CREA notices that accompany MLS® listing data, word
 *           for word, and the feed-aware source line for hub provenance. One
 *           copy: every page, footnote and disclaimer reads these.
 */

/** The §8.12.05 board representation, without the "NOTE: " lead. */
export const BOARD_REPRESENTATION =
  'This representation is based in whole or in part on data generated by the Chilliwack & District Real Estate Board, Fraser Valley Real Estate Board or Real Estate Board of Greater Vancouver which assumes no responsibility for its accuracy.';

/** MLS® Reciprocity Rules §8.12.05, exact notice for thumbnail and detail displays. */
export const MLS_RECIPROCITY_NOTICE = `NOTE: ${BOARD_REPRESENTATION}`;

/** CREA DDF® Rule 6(d)(i)(b) disclaimer. */
export const CREA_DDF_NOTICE =
  'The information contained on this site is based in whole or in part on information that is provided by members of The Canadian Real Estate Association (CREA), who are responsible for its accuracy. CREA reproduces and distributes this information as a service for its members and assumes no responsibility for its accuracy.';

export const CREA_TRADEMARK_NOTICE =
  'MLS®, Multiple Listing Service®, and REALTOR® are trademarks of the Canadian Real Estate Association (CREA).';

/** Board-only source line. "Live" is accurate only for the board feed. */
export const BOARD_PROVENANCE_SOURCE = 'Live MLS® data via board reciprocity';
export const DDF_PROVENANCE_SOURCE = "MLS® listing data from CREA's DDF®";
export const MIXED_PROVENANCE_SOURCE =
  "MLS® listing data from BC real estate boards (MLS® Reciprocity) and CREA's DDF®";

export interface MlsFeedPresence {
  board: boolean;
  ddf: boolean;
}

/**
 * Which feeds a set of listings came from. Unknown (no providers, or none
 * passed) counts as both, so a page that cannot tell carries both notices.
 */
export function mlsFeedPresence(feedProviders?: readonly (string | null | undefined)[] | null): MlsFeedPresence {
  if (!feedProviders || feedProviders.length === 0) return { board: true, ddf: true };
  const ddf = feedProviders.some((p) => p === 'ddf');
  const board = feedProviders.some((p) => p !== 'ddf');
  return { board, ddf };
}

export function mlsProvenanceSource(feeds: MlsFeedPresence): string {
  if (feeds.ddf && !feeds.board) return DDF_PROVENANCE_SOURCE;
  if (feeds.ddf) return MIXED_PROVENANCE_SOURCE;
  return BOARD_PROVENANCE_SOURCE;
}

/** The notices for the feeds present, trademark sentence last. */
export function mlsNotices(feeds: MlsFeedPresence): string[] {
  return [
    ...(feeds.board ? [MLS_RECIPROCITY_NOTICE] : []),
    ...(feeds.ddf ? [CREA_DDF_NOTICE] : []),
    CREA_TRADEMARK_NOTICE,
  ];
}

/** Board notice plus the CREA trademark sentence, for board-only footnotes. */
export const MLS_RECIPROCITY_FOOTNOTE = `${MLS_RECIPROCITY_NOTICE} ${CREA_TRADEMARK_NOTICE}`;

/** Both feeds' notices, for footnotes on pages whose listings can come from either feed. */
export const MLS_ALL_FEEDS_FOOTNOTE = mlsNotices({ board: true, ddf: true }).join(' ');
